/**
 * Phase A: single owner for notifications table, matches, and former Network realtime.
 * NetworkDataContext registers router callbacks; chat/messages untouched.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabaseGuard';
import type { BlissNetNetworkRouter } from './blissLiveSyncTypes';
import type { BlissNetNetworkRealtimeEvent } from './blissLiveSyncTypes.events';
import { BLISSNET_NOTIFICATIONS_CHANGED } from './blissnetLiveEvents';
import { processMatchRealtimeForBlissLive } from '@/utils/matchRealtimeBliss';

type BlissLiveSyncContextValue = {
  registerNetworkRouter: (router: BlissNetNetworkRouter) => () => void;
  /** notifications table rows keyed by id (current user only) */
  notificationRows: Record<string, any>;
  notificationsEpoch: number;
};

const BlissLiveSyncContext = createContext<BlissLiveSyncContextValue | null>(null);

export function useBlissLiveSync(): BlissLiveSyncContextValue {
  const ctx = useContext(BlissLiveSyncContext);
  if (!ctx) {
    throw new Error('useBlissLiveSync must be used within BlissLiveSyncProvider');
  }
  return ctx;
}

/** When Network may render without BlissLiveSync (tests); prefer useBlissLiveSync in app shell. */
export function useBlissLiveSyncOptional(): BlissLiveSyncContextValue | null {
  return useContext(BlissLiveSyncContext);
}

export function BlissLiveSyncProvider({ children }: { children: React.ReactNode }) {
  const { user, isReady } = useAuth() as any;
  const userId = user?.id || '';

  const routerRef = useRef<BlissNetNetworkRouter | null>(null);
  const registerNetworkRouter = useCallback((fn: BlissNetNetworkRouter) => {
    routerRef.current = fn;
    return () => {
      routerRef.current = null;
    };
  }, []);

  const [notificationRows, setNotificationRows] = useState<Record<string, any>>({});
  const [notificationsEpoch, setNotificationsEpoch] = useState(0);

  const bumpNotifications = useCallback(() => {
    setNotificationsEpoch((e) => e + 1);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(BLISSNET_NOTIFICATIONS_CHANGED, { detail: { ts: Date.now() } })
      );
    }
  }, []);

  useEffect(() => {
    if (!userId || !isReady) return;

    let debounceProfiles: ReturnType<typeof setTimeout> | null = null;

    const route = (e: BlissNetNetworkRealtimeEvent) => {
      void Promise.resolve(routerRef.current?.(e));
    };

    /** One route() per connections UPDATE — disconnect/block transitions win over approval. */
    const isDisconnectTransition = (newC: { status?: string } | null, oldC: { status?: string } | null) => {
      const isDisconnection =
        newC?.status === 'DISCONNECTED' && oldC?.status !== 'DISCONNECTED';
      const isBlocked = newC?.status === 'BLOCKED' && oldC?.status !== 'BLOCKED';
      return Boolean(isDisconnection || isBlocked);
    };

    const onConnectionsUpdateUserId = (payload: { new: any; old: any }) => {
      const newC = payload.new;
      const oldC = payload.old;
      if (isDisconnectTransition(newC, oldC)) {
        route({ type: 'connection_disconnect', payload, variant: 'self_update' });
        return;
      }
      route({ type: 'connection_approval', payload });
    };

    const onConnectionsUpdateConnUserId = (payload: { new: any; old: any }) => {
      const newC = payload.new;
      const oldC = payload.old;
      if (isDisconnectTransition(newC, oldC)) {
        route({ type: 'connection_disconnect', payload, variant: 'other_update' });
        return;
      }
      route({ type: 'connection_approval', payload });
    };

    const channel = supabase
      .channel(`bliss-live-sync-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles'
        },
        (payload: any) => {
          if (!payload.new?.discovery_visibility) return;
          if (payload.old?.discovery_visibility === payload.new?.discovery_visibility) return;
          if (debounceProfiles) clearTimeout(debounceProfiles);
          debounceProfiles = setTimeout(() => {
            debounceProfiles = null;
            route({ type: 'profiles_discovery_visibility', payload });
          }, 500);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'connection_requests',
          filter: `recipient_id=eq.${userId}`
        },
        (payload) => route({ type: 'connection_request', payload })
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'connection_requests',
          filter: `recipient_id=eq.${userId}`
        },
        (payload) => route({ type: 'connection_request', payload })
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'connection_requests',
          filter: `recipient_id=eq.${userId}`
        },
        (payload) => route({ type: 'connection_request', payload })
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'connections',
          filter: `user_id=eq.${userId}`
        },
        (payload) => route({ type: 'connection_approval', payload })
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'connections',
          filter: `connection_user_id=eq.${userId}`
        },
        (payload) => route({ type: 'connection_approval', payload })
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'connections',
          filter: `user_id=eq.${userId}`
        },
        onConnectionsUpdateUserId
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'connections',
          filter: `connection_user_id=eq.${userId}`
        },
        onConnectionsUpdateConnUserId
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'connections',
          filter: `connection_user_id=eq.${userId}`
        },
        (payload) =>
          route({ type: 'connection_disconnect', payload, variant: 'other_delete' })
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'connections',
          filter: `user_id=eq.${userId}`
        },
        (payload) =>
          route({ type: 'connection_disconnect', payload, variant: 'self_delete' })
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_events',
          filter: `user_id=eq.${userId}`
        },
        (payload) => route({ type: 'user_event_insert', payload })
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          const row = payload.new;
          if (row?.id) {
            setNotificationRows((prev) => ({ ...prev, [row.id]: row }));
          }
          bumpNotifications();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          const row = payload.new;
          if (row?.id) {
            setNotificationRows((prev) => ({ ...prev, [row.id]: row }));
          }
          bumpNotifications();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          const id = payload.old?.id;
          if (id) {
            setNotificationRows((prev) => {
              const next = { ...prev };
              delete next[id];
              return next;
            });
          }
          bumpNotifications();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'matches',
          filter: `user1_id=eq.${userId}`
        },
        (payload) => void processMatchRealtimeForBlissLive(userId, payload)
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'matches',
          filter: `user1_id=eq.${userId}`
        },
        (payload) => void processMatchRealtimeForBlissLive(userId, payload)
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'matches',
          filter: `user2_id=eq.${userId}`
        },
        (payload) => void processMatchRealtimeForBlissLive(userId, payload)
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'matches',
          filter: `user2_id=eq.${userId}`
        },
        (payload) => void processMatchRealtimeForBlissLive(userId, payload)
      )
      .subscribe();

    return () => {
      if (debounceProfiles) clearTimeout(debounceProfiles);
      supabase.removeChannel(channel);
    };
  }, [userId, isReady, bumpNotifications]);

  const value = useMemo(
    () => ({
      registerNetworkRouter,
      notificationRows,
      notificationsEpoch
    }),
    [registerNetworkRouter, notificationRows, notificationsEpoch]
  );

  return (
    <BlissLiveSyncContext.Provider value={value}>{children}</BlissLiveSyncContext.Provider>
  );
}
